/**
 * The race bib — the signature element of this design. SPEC.md §5.
 *
 * Perforated left edge, oversized number, and a top rule coloured by the
 * member's longest completed distance. Scroll the roster and you read the
 * club's fitness spread at a glance. It is the one bold thing, so everything
 * around it stays quiet.
 *
 * Restyled 26 Aug 2026 with the rest of the app: square corners, Archivo, warm
 * ground. Two things deliberately did NOT change, because they are the bib —
 * the perforation, and the distance ramp on the top rule. That rule encodes
 * DATA (d5 pale gold → d42 near-black), so it survives every reskin.
 */

import {
  bibClass,
  bibNo,
  DISTS,
  duesState,
  formatTime,
  personalBest,
  TIERS,
} from '../data';
import { useGrounds } from '../context/ClubContext';

/* bibClass() returns 't5' | 't10' | 't21' | 't42' | '' — mapped to the
   distance ramp on the top rule. No race yet = quiet grey. */
const RULE = {
  t5: 'border-t-d5',
  t10: 'border-t-d10',
  t21: 'border-t-d21',
  t42: 'border-t-d42',
};
const ruleClass = (cls) => RULE[cls] ?? 'border-t-line';

/** The dotted tear-off strip down the left edge. */
function Perforation({ dots = 5 }) {
  return (
    <div
      className="flex w-[17px] flex-none flex-col items-center justify-around border-r border-dashed border-line-strong bg-ground py-2.5"
      aria-hidden="true"
    >
      {Array.from({ length: dots }, (_, i) => (
        <i key={i} className="h-[3px] w-[3px] rounded-full bg-line-strong" />
      ))}
    </div>
  );
}

/** Dues status line under the PBs. Tone follows the dues state. */
function StatusLine({ tone, className = '', children }) {
  const toneCls =
    tone === 'bad' ? 'font-semibold text-flag-600' : tone === 'good' ? 'text-sea-600' : 'text-copy-muted';
  return <div className={`font-market text-[10px] ${toneCls} ${className}`}>{children}</div>;
}

/**
 * A member in the roster grid. The whole bib is the target — an admin taps
 * it to open the member.
 */
export function BibCard({ member, results, onOpen }) {
  const grounds = useGrounds();
  const dues = duesState(member);
  const ground = grounds[member.ground]?.split(' ')[0] ?? '—';

  // Longest three distances they have actually run, so the bib shows the
  // most impressive line rather than always 5K/10K.
  const pbs = DISTS.filter((d) => personalBest(results, member.id, d.key)).slice(-3);

  const statusTone = dues.cls === 'late' ? 'bad' : dues.cls === 'ok' ? 'good' : undefined;

  return (
    <button
      type="button"
      className="lift relative flex w-full overflow-hidden border border-line bg-white text-left hover:bg-ground-warm"
      onClick={onOpen ? () => onOpen(member.id) : undefined}
      aria-label={`Bib ${bibNo(member.bib)}, ${member.name}`}
    >
      <Perforation />
      <div className={`min-w-0 flex-1 border-t-[3px] p-4 ${ruleClass(bibClass(results, member.id))}`}>
        <div className="flex items-baseline justify-between gap-2.5">
          <div className="font-market text-[34px] font-extrabold leading-[0.85] tracking-[-0.03em] tabular-nums text-ink-700">
            {bibNo(member.bib)}
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <div className="truncate text-right font-market text-[15.5px] font-bold tracking-[-0.015em] text-ink-700">
              {member.name}
            </div>
            {member.photo && (
              <img
                className="h-[38px] w-[38px] flex-none border border-line object-cover"
                src={member.photo}
                alt=""
              />
            )}
          </div>
        </div>

        <div className="mt-2.5 flex flex-wrap gap-3 border-t border-line pt-2 font-market text-[10.5px] text-copy-muted">
          {pbs.length ? (
            pbs.map((d) => (
              <span key={d.key}>
                {d.key}{' '}
                <b className="text-ink-700">
                  {formatTime(personalBest(results, member.id, d.key).timeS)}
                </b>
              </span>
            ))
          ) : (
            <span className="text-line-strong">No race logged yet.</span>
          )}
        </div>

        <StatusLine tone={statusTone} className="mt-2">
          {TIERS[member.tier].label} · {ground} · {dues.label}
        </StatusLine>
      </div>
    </button>
  );
}

/**
 * The oversized bib at the top of a member's home screen and of the admin
 * member detail page. Same object, bigger type.
 *
 * `showDues` — the admin detail page shows dues status; the member's own home does not.
 */
export function MyBib({ member, results, showDues = false }) {
  const grounds = useGrounds();
  const dues = duesState(member);
  const statusTone = dues.cls === 'late' ? 'bad' : dues.cls === 'ok' ? 'good' : undefined;

  return (
    <div className="mb-3.5 flex overflow-hidden border border-line bg-white">
      <Perforation dots={6} />
      <div className={`min-w-0 flex-1 border-t-4 p-5 ${ruleClass(bibClass(results, member.id))}`}>
        <div className="font-market text-[58px] font-extrabold leading-[0.8] tracking-[-0.035em] tabular-nums text-ink-700">
          {bibNo(member.bib)}
        </div>
        <div className="mt-2.5 font-market text-[22px] font-bold tracking-[-0.025em] text-ink-700">
          {member.name}
        </div>
        <StatusLine tone={showDues ? statusTone : undefined} className="mt-2">
          {TIERS[member.tier].label} · {grounds[member.ground] ?? '—'}
          {showDues ? ` · ${dues.label}` : ''}
        </StatusLine>
      </div>
    </div>
  );
}

/**
 * The personal-best grid. Every distance on the ladder renders, including
 * ones they have not run — a blank is an invitation, so it reads
 * "Not run yet." rather than disappearing.
 */
export function PbGrid({ memberId, results }) {
  return (
    <div className="grid grid-cols-2 gap-px border border-line bg-line min-[600px]:grid-cols-4">
      {DISTS.map((d) => {
        const pb = personalBest(results, memberId, d.key);
        return (
          <div
            key={d.key}
            className={`border-t-[3px] bg-white p-4 ${ruleClass(d.cls)}`}
          >
            <div className="font-market text-[10.5px] font-bold uppercase tracking-[0.16em] text-copy-label">{d.key}</div>
            {pb ? (
              <>
                <div className="mt-1.5 font-market text-[20px] font-extrabold tracking-[-0.02em] tabular-nums text-ink-700">
                  {formatTime(pb.timeS)}
                </div>
                <div className="mt-1 font-market text-[10.5px] text-copy-label">
                  {formatDateShort(pb.date)}
                </div>
              </>
            ) : (
              <div className="mt-1 font-market text-[11.5px] text-line-strong">Not run yet.</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Local to keep the import list on this file short. */
function formatDateShort(iso) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });
}
